import React, { useState, useEffect } from 'react';
import { useAuth } from '../services/AuthContext';
import { useNavigate } from 'react-router-dom';
import * as api from '../services/api';
import {
    ButtonPrimary,
    StatCard,
    XPBar,
    StreakCounter,
    LessonCard,
    Badge,
    Spinner,
    Alert,
    Icon
} from '../components/ComponentLibrary';
import ScrollReveal from '../components/ScrollReveal';
import './StudentPortalNew.css';

export default function StudentPortalNew() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [portalData, setPortalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch student data
    useEffect(() => {
        const fetchPortalData = async () => {
            try {
                setLoading(true);
                const data = await api.getStudentDashboard();
                setPortalData(data);
            } catch (err) {
                console.error('Portal error:', err);
                setError('عفواً، فشل تحميل بيانات لوحة التحكم.');
            } finally {
                setLoading(false);
            }
        };

        fetchPortalData();
    }, []);

    // Calculate level from XP (gamification) - using total marks or attempts for now
    const calculateGamification = (stats) => {
        const totalAttempts = stats?.totalAttempts || 0;
        const totalPoints = totalAttempts * 20; // Example
        const level = Math.floor(totalPoints / 100) + 1;
        const nextLevelXP = level * 100;
        const currentXP = totalPoints;

        return { level, nextLevelXP, currentXP };
    };

    if (loading) {
        return (
            <div className="portal-loading">
                <Spinner size="lg" />
                <p className="loading-text">جاري تحضير عالمك البرمجي...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="portal-error-container">
                <Alert variant="error" onClose={() => window.location.reload()}>
                    {error}
                </Alert>
            </div>
        );
    }

    const { student, statistics, availableQuizzes, recentAttempts } = portalData || {};
    const { level, nextLevelXP, currentXP } = calculateGamification(statistics);
    const streak = 3; // In a real app, this would come from the backend

    return (
        <div className="portal-container">
            {/* 1. Header & Welcome Area */}
            <ScrollReveal direction="down" duration={0.8}>
                <header className="portal-header">
                    <div className="welcome-section">
                        <div className="welcome-badge">
                            <Icon name="sparkles" size={20} className="wave-icon" />
                            <span className="welcome-text">أهلاً بك مجدداً</span>
                        </div>
                        <h1 className="student-name">
                            {student?.fullName || user?.fullName || 'بطل كود ويف'}
                        </h1>
                        <div className="track-info">
                            <Badge variant="sky" size="lg" icon={<Icon name={(student?.track || user?.track) === 'scratch' ? 'play' : 'target'} size={14} />}>
                                {(student?.track || user?.track) === 'scratch' ? 'مسار Scratch' : 'مسار تطوير الويب'}
                            </Badge>
                            <ButtonPrimary onClick={() => navigate('/my-learning')} size="sm">
                                <Icon name="play" size={16} /> مجموعاتي وكورساتي
                            </ButtonPrimary>
                        </div>
                    </div>

                    <div className="stats-highlight">
                        <StreakCounter days={streak} />
                    </div>
                </header>
            </ScrollReveal>

            {/* 2. Level & Progress Section */}
            <section className="progress-section">
                <div className="progress-card">
                    <div className="level-badge-large">
                        <span className="level-label">المستوى</span>
                        <span className="level-number">{level}</span>
                    </div>
                    <div className="xp-container">
                        <div className="xp-header">
                            <span className="xp-title">نقاط الخبرة (XP)</span>
                            <span className="xp-value">{currentXP} / {nextLevelXP}</span>
                        </div>
                        <XPBar currentXP={currentXP} nextLevelXP={nextLevelXP} level={level} />
                    </div>
                </div>
            </section>

            {/* 3. Main Stats Grid */}
            <ScrollReveal direction="up" delay={0.2}>
                <section className="stats-grid">
                    <ScrollReveal direction="up" delay={0.1} distance={20}>
                        <StatCard
                            title="المحاولات الكلية"
                            value={statistics?.totalAttempts || 0}
                            icon="target"
                            trend="+2 هذا اليوم"
                            trendDirection="up"
                        />
                    </ScrollReveal>
                    <ScrollReveal direction="up" delay={0.2} distance={20}>
                        <StatCard
                            title="تحديات مكتملة"
                            value={statistics?.quizzesPassed || 0}
                            icon="check"
                            variant="success"
                        />
                    </ScrollReveal>
                    <ScrollReveal direction="up" delay={0.3} distance={20}>
                        <StatCard
                            title="متوسط الدرجات"
                            value={`${statistics?.averageScore?.toFixed(0) || 0}%`}
                            icon="star"
                            variant="sky"
                        />
                    </ScrollReveal>
                    <ScrollReveal direction="up" delay={0.4} distance={20}>
                        <StatCard
                            title="أفضل نتيجة"
                            value={`${statistics?.bestScore || 0}%`}
                            icon="progress"
                            variant="sunshine"
                        />
                    </ScrollReveal>
                </section>
            </ScrollReveal>

            {/* 4. Energy Flow Component (Animated Line) */}
            <div className="energy-connector">
                <div className="energy-orb"></div>
                <div className="energy-line"></div>
            </div>

            {/* 5. Inspirational Quotes */}
            <section className="inspiration-area">
                <InspirationalQuotes />
            </section>

            {/* 6. Active Missions (Learning Path) */}
            <ScrollReveal direction="up" delay={0.2}>
                <section className="missions-area">
                    <div className="area-header">
                        <h2 className="area-title">
                            <Icon name="target" size={24} className="title-icon" />
                            مسارك التعليمي
                        </h2>
                        <p className="area-subtitle">أكمل التحديات لتفتح آفاقاً جديدة</p>
                    </div>

                    <div className="cw-grid cw-grid-3">
                        {availableQuizzes && availableQuizzes.length > 0 ? (
                            availableQuizzes.map((quiz, index) => {
                                const isUnlocked = quiz.isTrackMatch;
                                const isCompleted = quiz.passed;

                                return (
                                    <ScrollReveal key={quiz.quizId} delay={0.1 * index} direction="up" distance={30}>
                                        <div className="mission-wrapper">
                                            <LessonCard
                                                title={quiz.title}
                                                description={quiz.description}
                                                icon={quiz.track === 'scratch' ? 'play' : 'target'}
                                                variant={quiz.track === 'scratch' ? 'sunshine' : 'ocean'}
                                                progress={isCompleted ? 100 : (quiz.attemptCount > 0 ? (quiz.attemptCount / quiz.maxAttempts) * 100 : 0)}
                                                onClick={() => isUnlocked && quiz.canAttempt ? navigate(`/take-quiz/${quiz.quizId}`) : null}
                                                className={!isUnlocked ? 'locked-card' : ''}
                                            />
                                            {!isUnlocked && (
                                                <div className="card-overlay">
                                                    <Icon name="lock" size={48} className="lock-icon" />
                                                    <span className="lock-text">هذا المحتوى مغلق حالياً</span>
                                                </div>
                                            )}
                                            <div className="mission-meta">
                                                {isCompleted ? (
                                                    <Badge variant="success">تم الإنجاز <Icon name="check" size={14} className="mr-1" /></Badge>
                                                ) : isUnlocked ? (
                                                    <Badge variant="ocean">مهمة نشطة <Icon name="rocket" size={14} className="mr-1" /></Badge>
                                                ) : (
                                                    <Badge variant="gray">مغلق <Icon name="lock" size={14} className="mr-1" /></Badge>
                                                )}
                                            </div>
                                        </div>
                                    </ScrollReveal>
                                );
                            })
                        ) : (
                            <div className="empty-missions">
                                <div className="empty-icon"><Icon name="mail" size={48} /></div>
                                <h3>لا توجد مهمات متاحة حالياً</h3>
                                <p>سيقوم المعلم بإضافة مهمات جديدة قريباً.</p>
                            </div>
                        )}
                    </div>
                </section>
            </ScrollReveal>

            {/* 7. Recent History */}
            {recentAttempts && recentAttempts.length > 0 && (
                <section className="history-section">
                    <div className="area-header">
                        <h2 className="area-title"><Icon name="clock" size={24} /> آخر النشاطات</h2>
                    </div>
                    <div className="history-list">
                        {recentAttempts.slice(0, 5).map((attempt, idx) => (
                            <div key={idx} className="history-item">
                                <div className="history-info">
                                    <span className="history-quiz-title">{attempt.quizId?.title || 'تحدي برمجي'}</span>
                                    <span className="history-date">{new Date(attempt.completedAt).toLocaleDateString('ar-EG')}</span>
                                </div>
                                <div className="history-result">
                                    <span className="history-score">{attempt.percentage}%</span>
                                    <Badge variant={attempt.passed ? 'success' : 'error'}>
                                        {attempt.passed ? 'ناجح' : 'حاول مرة أخرى'}
                                    </Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

// Sub-component for Quotes
function InspirationalQuotes() {
    const [currentQuote, setCurrentQuote] = useState(0);
    const quotes = [
        "كل مبرمج عظيم بدأ بسطر كود واحد.. وأنت قادر تبقى منهم! 💪",
        "الخطأ في البرمجة مش فشل.. ده أول خطوة للنجاح! 🚀",
        "أنت مش بتتعلم برمجة بس.. أنت بتبني مستقبلك! ⭐",
        "كل تحدي بتعديه بيقربك خطوة من أحلامك! 🎯",
        "الإبداع مالوش حدود.. وأنت عندك كل الأدوات! 🌟",
        "مفيش حاجة اسمها صعب.. في حاجة اسمها محتاجة وقت وتدريب! 💡",
        "كل لحظة بتقضيها في التعلم هي استثمار في مستقبلك! 📚"
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentQuote((prev) => (prev + 1) % quotes.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [quotes.length]);

    return (
        <div className="quotes-card">
            <div className="quote-icon">
                <Icon name="sparkles" size={32} />
            </div>
            <div className="quote-content">
                <p className="quote-text">{quotes[currentQuote]}</p>
            </div>
            <div className="quote-dots">
                {quotes.map((_, i) => (
                    <div key={i} className={`quote-dot ${i === currentQuote ? 'active' : ''}`}></div>
                ))}
            </div>
        </div>
    );
}
